import { Checkbox } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { BorrowingBaseGroupListForm } from "features/credit-facility/borrowing-base/BorrowingBaseGroupsFormList";
import {
  getCreditFacilityRetrieveQueryKey,
  useCreditFacilityEdit,
  useCreditFacilityRetrieveSuspense,
} from "gen/endpoints/credit-facility/credit-facility";
import { useCreditFacilityBorrowingBaseGroupListSuspense } from "gen/endpoints/credit-facility-borrowing-base-group/credit-facility-borrowing-base-group";
import { useTransactionCalculationListSuspense } from "gen/endpoints/transactions/transactions";
import { CreditFacilityBorrowingBaseGroupCreateAPIDataRequest } from "gen/model/creditFacilityBorrowingBaseGroupCreateAPIDataRequest";
import { CreditFacilityBorrowingBaseGroupUpsertData } from "gen/model/creditFacilityBorrowingBaseGroupUpsertData";
import { CreditFacilityEditDataRequest } from "gen/model/creditFacilityEditDataRequest";
import { CreditFacilityUpsertDataRequest } from "gen/model/creditFacilityUpsertDataRequest";
import { cloneDeep } from "lodash-es";
import { useCallback, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  ContentBlock,
  ContentBlockBody,
  ContentBlockGroup,
  ContentBlockHeader,
  ContentBlockTitle,
} from "shared/components/content/ContentBlock";
import Form, { FormHandle } from "shared/components/form/Form";
import { FormSelect } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { PageWizardFormFooter } from "shared/components/layout/PageWizard";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";

export const CreditFacilitySetupBorrowingBase: React.FC = () => {
  const { t } = useTranslation(["creditFacility"]);
  const { fundFamilyId, creditFacilityId } = useRequiredStringParams();
  const { data: creditFacility } = useCreditFacilityRetrieveSuspense(
    fundFamilyId,
    creditFacilityId,
  );
  const { data: calculations } =
    useTransactionCalculationListSuspense(fundFamilyId);
  const queryClient = useQueryClient();
  const form = useForm<CreditFacilityUpsertDataRequest>({
    defaultValues: creditFacility,
  });
  const calculationValue = form.watch("borrowing_base_unfunded_calculation_id");
  const { mutateAsync: updateCreditFacility } = useCreditFacilityEdit();
  const creditFacilityForm = useRef<FormHandle>(null);
  const groupsForm = useRef<FormHandle>(null);
  const { data: borrowingGroupData } =
    useCreditFacilityBorrowingBaseGroupListSuspense(
      fundFamilyId,
      creditFacilityId,
      {
        version_date: creditFacility.inception_date,
      },
      {
        query: {
          select: useCallback(
            (
              data: CreditFacilityBorrowingBaseGroupUpsertData[],
            ): CreditFacilityBorrowingBaseGroupCreateAPIDataRequest => ({
              borrowing_base_groups: data.length
                ? cloneDeep(data)
                : [
                    {
                      credit_facility_id: creditFacilityId,
                      name: "",
                      rate: "",
                      id: "",
                      individual_concentration_limit: null,
                      aggregate_concentration_limit: null,
                    },
                  ],
            }),
            [creditFacilityId],
          ),
        },
      },
    );
  // if any borrowing base group has a concentration limit, then the borrowing bases use concentration limits
  const [hasConcentrationLimit, setHasConcentrationLimit] = useState(
    useMemo(
      () =>
        borrowingGroupData.borrowing_base_groups.some(
          (group) =>
            !!Number(group.individual_concentration_limit) ||
            !!Number(group.aggregate_concentration_limit),
        ),
      [borrowingGroupData.borrowing_base_groups],
    ),
  );

  return (
    <PageContent>
      <PageHeader
        workflowName={t("creditFacility:setup.title")}
        title={t("creditFacility:setup.borrowingBase.title")}
      />
      <PageBody>
        <Form
          formRef={creditFacilityForm}
          form={form}
          onSubmit={async (data: CreditFacilityEditDataRequest) => {
            const newData = await updateCreditFacility({
              fundFamilyId,
              creditFacilityId,
              data,
              params: {
                version_date: creditFacility.inception_date,
              },
            });
            queryClient.setQueryData(
              getCreditFacilityRetrieveQueryKey(fundFamilyId, creditFacilityId),
              newData,
            );
          }}
        >
          <ContentBlockGroup allowToggle={false}>
            <ContentBlock>
              <ContentBlockHeader>
                <ContentBlockTitle>
                  {t("creditFacility:borrowingBase.configurationListItem")}
                </ContentBlockTitle>
              </ContentBlockHeader>
              <ContentBlockBody maxWidth="600px">
                <FormSelect
                  name="borrowing_base_unfunded_calculation_id"
                  title={t("creditFacility:borrowingBase.calculation")}
                >
                  {calculations.map(({ id, name }) => (
                    <SelectOption key={id} value={id}>
                      {name}
                    </SelectOption>
                  ))}
                </FormSelect>
                <Checkbox
                  onChange={() => {
                    setHasConcentrationLimit(!hasConcentrationLimit);
                  }}
                  isChecked={hasConcentrationLimit}
                >
                  {t(
                    "creditFacility:borrowingBase.concentrationLimits.hasConcentrationLimits",
                  )}
                </Checkbox>
              </ContentBlockBody>
            </ContentBlock>
          </ContentBlockGroup>
        </Form>
        <BorrowingBaseGroupListForm
          formRef={groupsForm}
          borrowingBaseGroupData={borrowingGroupData}
          hasConcentrationLimit={hasConcentrationLimit}
          shouldSelectInvestors={async () => {
            if (!calculationValue) {
              form.setError("borrowing_base_unfunded_calculation_id", {
                message: t(
                  "creditFacility:borrowingBase.calculationInputError",
                ),
              });
              return false;
            }
            await creditFacilityForm.current?.submit();
            await queryClient.invalidateQueries({
              queryKey: getCreditFacilityRetrieveQueryKey(
                fundFamilyId,
                creditFacilityId,
              ),
            });
            return true;
          }}
        />
        <PageWizardFormFooter
          onClickNext={async () => {
            await creditFacilityForm.current?.submit();
            await groupsForm.current?.submit();
          }}
        />
      </PageBody>
    </PageContent>
  );
};
